import { useEffect, useRef, useState } from "react";
import { isBirthdayEventActive } from "@/lib/birthdayEvent";

/**
 * Founder's Birthday Event
 * -------------------------
 * Purely cosmetic, self-gated banner + confetti burst. See
 * `src/lib/birthdayEvent.ts` for the active date window — once that
 * window passes, this component simply stops rendering anything.
 *
 * No new dependency was added: confetti is a small dependency-free
 * canvas particle burst.
 */

const CONFETTI_COLORS = [
  "hsl(51, 100%, 50%)", // gold (brand primary)
  "hsl(51, 100%, 70%)",
  "hsl(190, 100%, 60%)", // player glow
  "hsl(0, 100%, 70%)", // opponent glow
  "#ffffff",
];

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rotation: number;
  rotationSpeed: number;
  shape: 0 | 1;
}

function ConfettiBurst() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    const particleCount = 160;
    const particles: Particle[] = Array.from({ length: particleCount }).map(() => ({
      x: Math.random() * width,
      y: -20 - Math.random() * height * 0.5,
      vx: (Math.random() - 0.5) * 1.8,
      vy: 0.65 + Math.random() * 1.15,
      size: 6 + Math.random() * 6,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      shape: Math.random() > 0.5 ? 1 : 0,
    }));

    let frame: number;
    const gravity = 0.008;
    const start = performance.now();
    const durationMs = 9000;

    const tick = (now: number) => {
      const elapsed = now - start;
      ctx.clearRect(0, 0, width, height);

      for (const p of particles) {
        p.vy += gravity;
        p.x += p.vx;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.fillStyle = p.color;
        if (p.shape === 0) {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.restore();
      }

      if (elapsed < durationMs) {
        frame = requestAnimationFrame(tick);
      } else {
        ctx.clearRect(0, 0, width, height);
      }
    };

    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-[9999]"
    />
  );
}

export function BirthdayEvent() {
  const [active, setActive] = useState(() => isBirthdayEventActive());
  const [dismissed, setDismissed] = useState(false);
  const [showConfetti, setShowConfetti] = useState(active);

  useEffect(() => {
    // Re-check periodically in case the app stays open across the
    // window boundary (event turning on or off without a refresh).
    const interval = setInterval(() => {
      setActive(isBirthdayEventActive());
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!active) {
      setShowConfetti(false);
      return;
    }
    setShowConfetti(true);
    const timeout = setTimeout(() => setShowConfetti(false), 9200);
    return () => clearTimeout(timeout);
  }, [active]);

  if (!active) return null;

  return (
    <>
      {showConfetti && <ConfettiBurst />}
      {!dismissed && (
        <div
          role="status"
          className="pointer-events-none fixed inset-x-0 top-0 z-[9998] flex justify-center px-4 pt-16 sm:pt-3"
        >
          <div className="pointer-events-none flex items-center gap-3 rounded-full border border-primary/40 bg-background/90 px-5 py-2 shadow-[0_0_20px_hsl(51_100%_50%/0.35)] backdrop-blur">
            <span className="font-display font-bold text-sm md:text-base text-glow-gold">
              🎉 IT&apos;S THE FOUNDER&apos;S BIRTHDAY TODAY — 20TH JULY! Thanks for playing!
            </span>
            <button
              type="button"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss birthday banner"
              className="pointer-events-auto ml-1 text-muted-foreground hover:text-foreground transition-colors text-sm leading-none"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </>
  );
}
